import { Link } from 'react-router-dom'
import type { PatientPrescription } from '../../store/patientDetailSlice'

interface PrescriptionHistoryTabProps {
  prescriptions: PatientPrescription[]
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return ''
  const dateOnly = dateStr.split('T')[0]
  const d = new Date(dateOnly + 'T00:00:00')
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

function getStatusStyle(status: string): string {
  switch (status) {
    case 'Active':
      return 'bg-accent-subtle-bg text-status-success'
    case 'Completed':
      return 'bg-border text-text-muted'
    case 'Discontinued':
      return 'bg-[#f0dede] text-status-error'
    case 'Expired':
      return 'bg-[#f0e8d0] text-status-warning'
    default:
      return 'bg-border text-text-muted'
  }
}

export function PrescriptionHistoryTab({ prescriptions }: PrescriptionHistoryTabProps) {
  return (
    <div data-testid="prescription-history-tab">
      {prescriptions.length === 0 ? (
        <p className="text-[13px] text-text-muted py-4" data-testid="no-prescription-history">
          No prescription history
        </p>
      ) : (
        <div className="grid grid-cols-1" style={{ gridTemplateRows: '36px repeat(auto-fill, 44px)' }}>
          <div className="rxh-table-header grid items-center h-9 text-[12px] font-medium text-text-muted" style={{ gridTemplateColumns: '1fr 100px 80px 100px 110px' }}>
            <span className="px-2">Medication</span>
            <span className="px-2">Dosage</span>
            <span className="px-2">Frequency</span>
            <span className="px-2">Start Date</span>
            <span className="px-2">Status</span>
          </div>
          {prescriptions.map((p) => {
            const rxId = `RX-${String(p.prescription_number).padStart(4, '0')}`
            return (
              <Link
                key={p.id}
                to={`/prescriptions/${p.id}`}
                data-testid="prescription-history-item"
                className="rxh-table-row grid items-center h-11 text-[13px] text-text-primary hover:bg-hover rounded-[4px] no-underline cursor-pointer"
                style={{ gridTemplateColumns: '1fr 100px 80px 100px 110px' }}
              >
                <span className="px-2" data-testid="prescription-medication">
                  <span className="text-text-muted font-[450] mr-2">{rxId}</span>
                  {p.medication_name}
                </span>
                <span className="px-2 text-text-muted" data-testid="prescription-dosage">{p.dosage || '—'}</span>
                <span className="px-2 text-text-muted" data-testid="prescription-frequency">{p.frequency || '—'}</span>
                <span className="px-2 text-text-muted" data-testid="prescription-start-date">{formatDate(p.start_date)}</span>
                <span className="px-2">
                  <span
                    data-testid="prescription-status-badge"
                    className={`inline-flex items-center h-5 px-1.5 rounded-[3px] text-[11px] font-medium ${getStatusStyle(p.status)}`}
                  >
                    {p.status}
                  </span>
                </span>
              </Link>
            )
          })}
        </div>
      )}
    </div>
  )
}
